import { zodResolver } from '@hookform/resolvers/zod';
import { always, cond, equals, find, propEq, values } from 'ramda';
import React, { FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { useTimer } from 'use-timer';
import { z } from 'zod';

import BasicText from '@ui/atoms/BasicText';
import CodeInput from '@ui/atoms/CodeInput/CodeInput.component';
import FlexColumn from '@ui/atoms/FlexColumn';
import FlexRow from '@ui/atoms/FlexRow';
import FolxLoader from '@ui/atoms/FolxLoader';
import HeadingText from '@ui/atoms/HeadingText';
import TextButton from '@ui/atoms/TextButton';

import MessageIconCard from '@ui/molecules/MessageIconCard';
import { IMessageIconCardProps } from '@ui/molecules/MessageIconCard/MessageIconCard.component';

import aligned from '@ui/styled/Aligned';

import { interpolate } from '@utils/butter';

import { AuthStep, AuthStepContext, useAuth } from '@providers/AuthProvider';

import CheckCirle from '@assets/vectors/check-circle-solid.inline.svg';
import ExclamationCircle from '@assets/vectors/exclamation-circle-solid.inline.svg';
import InfoCircle from '@assets/vectors/info-circle-solid.inline.svg';

import awsAuthErrorMessages, { AwsAuthErrorMessageValue } from '../awsAuthErrorMessages';

const AlignedRow = aligned(FlexRow);

interface ISMSCodeInputFormProps {
	authStep: AuthStepContext;
	setAuthStep: (authContext: AuthStepContext) => void;
	pageContent: ISMSCodeInputFormContent;
	phoneNumber: string;
	onComplete: () => void;
	changePhoneNumber?: () => void;
	resendCode: () => Promise<void>;
}

export interface ISMSCodeInputFormContent {
	title: string;
	changeNumberLink: string;
	changeNumberHref: string;
	description: string;
	codeSentMessage: string;
	codeWaitingMessage: string;
	resendLink: string;
}

const InlineLink = styled.a`
	color: black;
	text-decoration: underline;
`;

const InlineTextButton = styled(TextButton)`
	display: inline;
`;

const RESEND_CODE_TIMER = 15;
type CodeSentStatus = 'UNSENT' | 'WAITING' | 'SUCCESS' | 'ERROR';

const SMSCodeInputForm: FC<ISMSCodeInputFormProps> = ({
	authStep,
	setAuthStep,
	pageContent,
	phoneNumber,
	onComplete,
	changePhoneNumber,
	resendCode,
}) => {
	const { verifySMSMultifactor, verifySMSMultifactorSetup, signIn } = useAuth();

	const [submitting, setSubmitting] = useState(false);
	const [refetchTimer, setRefetchTimer] = useState(0);
	const [codeSentStatus, setCodeSentStatus] = useState<CodeSentStatus>('UNSENT');
	const [codeSentError, setCodeSentError] = useState('');

	const { time, start, status } = useTimer({
		initialTime: 0,
		endTime: refetchTimer,
	});

	const schema = z.object({
		verificationCode: z.string().min(6, { message: 'A valid verification code is required.' }),
	});

	type Schema = Partial<z.input<typeof schema>>;

	const {
		handleSubmit,
		setError,
		control,
		formState: { errors },
	} = useForm<Schema>({
		resolver: zodResolver(schema),
		mode: 'onSubmit',
		reValidateMode: 'onSubmit',
	});

	const {
		title,
		description,
		changeNumberLink,
		changeNumberHref,
		codeSentMessage,
		codeWaitingMessage,
		resendLink,
	} = pageContent;

	const getCodeSentMessage = cond<CodeSentStatus, boolean, IMessageIconCardProps | undefined>([
		[
			equals('WAITING'),
			() => {
				return {
					message: codeWaitingMessage,
					bgColor: 'lightGray',
					Icon: InfoCircle,
					iconAlt: 'Info',
				};
			},
		],
		[
			equals('SUCCESS'),
			() => {
				return {
					message: codeSentMessage,
					bgColor: 'greenLight',
					Icon: CheckCirle,
					iconAlt: 'Checkmark',
				};
			},
		],
		[
			equals('ERROR'),
			() => {
				return {
					message: codeSentError,
					bgColor: 'cardRed',
					Icon: ExclamationCircle,
					iconAlt: 'Exclamation',
				};
			},
		],
		[equals('UNSENT'), always(undefined)],
	])(codeSentStatus);

	const resendCodeWrapper = async () => {
		try {
			await resendCode();
			setCodeSentStatus('SUCCESS');
			setRefetchTimer(RESEND_CODE_TIMER);
			start();
		} catch (e) {
			setCodeSentStatus('ERROR');
			setCodeSentError(e?.message ?? awsAuthErrorMessages.errorSendingNewCode.userErrorMessage);
		}
	};

	const onSubmit = async (data: Schema) => {
		try {
			setSubmitting(true);
			let newAuthStep = authStep;
			if (authStep.nextStep === AuthStep.needSMSMultifactorVerification) {
				newAuthStep = await verifySMSMultifactor(authStep.user, data.verificationCode as string);
			} else if (authStep.nextStep === AuthStep.needSMSMultifactorSetupVerification) {
				newAuthStep = await verifySMSMultifactorSetup(
					authStep.user,
					data.verificationCode as string
				);
			}

			if (newAuthStep.nextStep === AuthStep.done) {
				onComplete();
			}

			setAuthStep(newAuthStep);
		} catch (e: any) {
			const errorMessage: AwsAuthErrorMessageValue | undefined = find<AwsAuthErrorMessageValue>(
				propEq('awsErrorMessage', e?.message ?? ''),
				values(awsAuthErrorMessages)
			);

			switch (e?.code) {
				case 'NotAuthorizedException':
					if (
						e?.message === awsAuthErrorMessages.expiredSession.awsErrorMessage ||
						e?.message === awsAuthErrorMessages.invalidSession.awsErrorMessage
					) {
						setAuthStep({ nextStep: AuthStep.needPassword });
						break;
					} else {
						setError('verificationCode', {
							type: 'manual',
							message:
								errorMessage?.userErrorMessage ??
								awsAuthErrorMessages.invalidSmsCode.userErrorMessage,
						});
					}
					break;
				case 'ExpiredCodeException':
					setError('verificationCode', {
						type: 'manual',
						message: awsAuthErrorMessages.expiredSmsCode.userErrorMessage,
					});
					break;
				default:
					setError('verificationCode', {
						type: 'manual',
						message:
							errorMessage?.userErrorMessage ??
							awsAuthErrorMessages.invalidSmsCode.userErrorMessage,
					});
			}
		} finally {
			setSubmitting(false);
		}
	};

	if (submitting) {
		return <FolxLoader />;
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<FlexColumn gap="xl">
				<FlexColumn gap="sm">
					<HeadingText>{title}</HeadingText>
					<BasicText>
						{description && interpolate(description, { phoneNumber })}{' '}
						{changePhoneNumber ? (
							<InlineTextButton
								variant="text"
								underline
								onClick={changePhoneNumber}
								label={changeNumberLink}
							/>
						) : (
							<InlineLink href={changeNumberHref} target="__blank">
								{changeNumberLink}
							</InlineLink>
						)}
					</BasicText>
				</FlexColumn>
				<Controller
					name="verificationCode"
					control={control}
					defaultValue=""
					render={({ field }) => (
						<CodeInput
							maxLength={6}
							{...field}
							error={errors?.verificationCode?.message}
							onComplete={handleSubmit(onSubmit)}
						/>
					)}
				/>
				{codeSentStatus !== 'UNSENT' && (
					<MessageIconCard
						message={getCodeSentMessage?.message}
						Icon={getCodeSentMessage?.Icon}
						iconAlt={getCodeSentMessage?.iconAlt}
						bgColor={getCodeSentMessage?.bgColor}
					/>
				)}
				<AlignedRow justifyContent="center">
					{status === 'RUNNING' && (
						<BasicText>
							{resendLink} in {refetchTimer - time} secs
						</BasicText>
					)}
					{status !== 'RUNNING' && (
						<TextButton
							disabled={codeSentStatus === 'WAITING'}
							type="button"
							variant="text"
							underline
							label={resendLink}
							onClick={() => {
								setCodeSentStatus('WAITING');
								resendCodeWrapper();
							}}
						/>
					)}
				</AlignedRow>
			</FlexColumn>
		</form>
	);
};

export default SMSCodeInputForm;
